<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- ───────────────────────────────────────────
           MOBILE HEADER
      ─────────────────────────────────────────────── -->
      <div class="md:hidden -mx-0">
        <!-- Gradient hero -->
        <div class="bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 px-4 pt-3 pb-6">
          <div class="flex items-start justify-between mb-4">
            <div>
              <h1 class="text-xl font-bold text-white">Customers</h1>
              <p class="text-emerald-200 text-xs mt-0.5">{{ periodLabel }}</p>
            </div>
            <!-- Filters button -->
            <button @click="mobileFiltersOpen = true"
              class="relative flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-xs font-semibold px-3.5 py-2 rounded-xl transition">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
              <span v-if="mobileFilterBadge > 0"
                class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-emerald-600 text-[10px] font-bold flex items-center justify-center shadow">
                {{ mobileFilterBadge }}
              </span>
            </button>
          </div>

          <!-- Hero stats (3 primary KPIs) -->
          <div v-if="!loading" class="grid grid-cols-3 gap-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Receivable</p>
              <p class="text-sm font-bold text-white whitespace-nowrap">{{ compactNum(kpis.total_receivable) }}</p>
              <p class="text-[9px] text-emerald-200 mt-0.5">to receive</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-red-200 uppercase tracking-widest mb-1">Payable</p>
              <p class="text-sm font-bold text-white whitespace-nowrap">{{ compactNum(kpis.total_payable) }}</p>
              <p class="text-[9px] text-emerald-200 mt-0.5">to give</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-white uppercase tracking-widest mb-1">Balance</p>
              <p class="text-sm font-bold text-white">{{ compactNum(kpis.net_balance) }}</p>
              <p class="text-[9px] text-emerald-200 mt-0.5">net</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-3 gap-2">
            <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Pull-up content card -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-4">

          <!-- Secondary KPI cards (2-col grid) -->
          <div v-if="!loading" class="grid grid-cols-2 gap-2 mb-4">
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Total Customers</p>
              <p class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ kpis.total_customers || 0 }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">active</p>
            </div>
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Inactive</p>
              <p class="text-sm font-bold text-amber-500">{{ kpis.inactive_count || 0 }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">last 30 days</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-2 gap-2 mb-4">
            <div v-for="i in 2" :key="i"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 space-y-2">
              <div class="h-2 w-14 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2 w-10 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
          </div>

          <!-- Charts row -->
          <div class="grid grid-cols-1 gap-3 mb-4">
            <!-- Doughnut -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3">Receivable vs Payable</p>
              <div v-if="loading" class="flex items-center justify-center h-32">
                <div class="w-32 h-32 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse"></div>
              </div>
              <div v-else class="flex items-center gap-6">
                <div class="shrink-0">
                  <Doughnut :data="customerDoughnutData" :options="doughnutOptions" style="width:130px;height:130px" />
                </div>
                <div class="flex-1 space-y-2.5">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
                      <span class="text-xs text-slate-500 dark:text-slate-400">Receivable</span>
                    </div>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(kpis.total_receivable) }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0"></span>
                      <span class="text-xs text-slate-500 dark:text-slate-400">Payable</span>
                    </div>
                    <span class="text-xs font-bold text-red-500 dark:text-red-400">{{ shortNum(kpis.total_payable) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- City distribution -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3">Customers by City</p>
              <div v-if="loading" class="flex items-end gap-1.5 h-32">
                <div v-for="i in 8" :key="i" class="flex-1 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"
                  :style="{ height: (30 + Math.random() * 70) + '%' }"></div>
              </div>
              <Bar v-else :data="cityChartData" :options="cityBarOptions" style="max-height:130px" />
            </div>
          </div>

          <!-- Top performers -->
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Top Performers</p>
          <div class="space-y-2 mb-4">
            <!-- Top by sales -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">Top by Sales</p>
              <div v-if="loading" class="space-y-2">
                <div v-for="i in 3" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
              </div>
              <div v-else-if="topBySales.length === 0" class="text-xs text-slate-400 text-center py-4">No data</div>
              <div v-else class="space-y-3">
                <div v-for="c in topBySales.slice(0, 3)" :key="c.name">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400 ml-2 shrink-0">{{ shortNum(c.sales) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-indigo-500 rounded-full" :style="{ width: calculatePercentage(c.sales, topBySales[0].sales) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top by receivable -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">Top by Receivable</p>
              <div v-if="loading" class="space-y-2">
                <div v-for="i in 3" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
              </div>
              <div v-else-if="topByReceivable.length === 0" class="text-xs text-slate-400 text-center py-4">No data</div>
              <div v-else class="space-y-3">
                <div v-for="c in topByReceivable.slice(0, 3)" :key="c.name">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(c.receivable) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" :style="{ width: calculatePercentage(c.receivable, topByReceivable[0].receivable) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top by profit -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">Most Profitable</p>
              <div v-if="loading" class="space-y-2">
                <div v-for="i in 3" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
              </div>
              <div v-else-if="topByProfit.length === 0" class="text-xs text-slate-400 text-center py-4">No data</div>
              <div v-else class="space-y-3">
                <div v-for="c in topByProfit.slice(0, 3)" :key="c.name">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(c.profit) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" :style="{ width: calculatePercentage(c.profit, topByProfit[0].profit) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Lowest by profit -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">Lowest by Profit</p>
              <div v-if="loading" class="space-y-2">
                <div v-for="i in 3" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
              </div>
              <div v-else-if="lowestByProfit.length === 0" class="text-xs text-slate-400 text-center py-4">No data</div>
              <div v-else class="space-y-3">
                <div v-for="c in lowestByProfit.slice(0, 3)" :key="c.name">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold ml-2 shrink-0"
                      :class="c.profit >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'">
                      {{ c.profit < 0 ? '-' : '' }}{{ shortNum(Math.abs(c.profit)) }}
                    </span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full rounded-full"
                      :class="c.profit >= 0 ? 'bg-amber-400' : 'bg-red-500'"
                      :style="{ width: calculateLowestProfitPct(c.profit) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Inactive Customers Mobile -->
          <div class="mb-4">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
              Inactive Customers
              <span v-if="!loading && filteredAndSortedInactive.length > 0" class="text-slate-500 font-normal ml-1">
                ({{ filteredAndSortedInactive.length }})
              </span>
            </p>
            <div v-if="loading" class="space-y-2">
              <div v-for="i in 3" :key="i" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 h-20 animate-pulse"></div>
            </div>
            <div v-else-if="inactiveCustomers.length === 0" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 text-xs text-slate-400 text-center">
              No inactive customers
            </div>
            <div v-else class="space-y-2">
              <div v-for="c in paginatedInactiveCustomers" :key="c.id"
                class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
                <div class="flex items-start justify-between mb-2">
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-slate-800 dark:text-white truncate">{{ c.name }}</p>
                    <p class="text-[10px] text-slate-400">{{ c.shop }}</p>
                  </div>
                  <span class="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 shrink-0">
                    {{ c.days_since }}d idle
                  </span>
                </div>
                <div class="grid grid-cols-3 gap-2 text-center pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div>
                    <p class="text-[9px] text-emerald-600 dark:text-emerald-400 font-semibold">Receivable</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.receivable) }}</p>
                  </div>
                  <div>
                    <p class="text-[9px] text-red-500 dark:text-red-400 font-semibold">Payable</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.payable) }}</p>
                  </div>
                  <div>
                    <p class="text-[9px] text-violet-600 dark:text-violet-400 font-semibold">Profit</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.profit) }}</p>
                  </div>
                </div>
              </div>
              <!-- Pagination for Inactive Mobile -->
              <div v-if="inactiveTotalPages > 1" class="flex items-center justify-center gap-1 pt-2">
                <button @click="inactivePage--" :disabled="inactivePage === 1"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                  :class="inactivePage === 1 ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <span class="text-xs text-slate-600 dark:text-slate-400 px-2">{{ inactivePage }} / {{ inactiveTotalPages }}</span>
                <button @click="inactivePage++" :disabled="inactivePage === inactiveTotalPages"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                  :class="inactivePage === inactiveTotalPages ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          </div>

          <!-- All Customers Mobile -->
          <div class="mb-4">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
              All Customers
              <span v-if="!loading && filteredAndSortedCustomers.length > 0" class="text-slate-500 font-normal ml-1">
                ({{ filteredAndSortedCustomers.length }})
              </span>
            </p>
            <div v-if="loading" class="space-y-2">
              <div v-for="i in 3" :key="i" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 h-24 animate-pulse"></div>
            </div>
            <div v-else-if="allCustomers.length === 0" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 text-xs text-slate-400 text-center">
              No customer data for this period
            </div>
            <div v-else class="space-y-2">
              <div v-for="c in paginatedAllCustomers" :key="c.id"
                class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
                <div class="flex items-start justify-between mb-2">
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-slate-800 dark:text-white truncate">{{ c.name }}</p>
                    <p class="text-[10px] text-slate-400">{{ c.shop }} • {{ c.city }}</p>
                  </div>
                  <span class="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0"
                    :class="c.margin >= 30 ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400' :
                            c.margin >= 15 ? 'bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400' :
                            c.margin >= 0 ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400' :
                            'bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400'">
                    {{ c.margin }}%
                  </span>
                </div>
                <div class="grid grid-cols-4 gap-2 text-center pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div>
                    <p class="text-[9px] text-slate-500 dark:text-slate-400 font-semibold">Sales</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.business) }}</p>
                  </div>
                  <div>
                    <p class="text-[9px] text-emerald-600 dark:text-emerald-400 font-semibold">Recv.</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.receivable) }}</p>
                  </div>
                  <div>
                    <p class="text-[9px] text-red-500 dark:text-red-400 font-semibold">Pay.</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.payable) }}</p>
                  </div>
                  <div>
                    <p class="text-[9px] text-violet-600 dark:text-violet-400 font-semibold">Profit</p>
                    <p class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(c.profit) }}</p>
                  </div>
                </div>
              </div>
              <!-- Pagination for All Customers Mobile -->
              <div v-if="customersTotalPages > 1" class="flex items-center justify-center gap-1 pt-2">
                <button @click="customersPage--" :disabled="customersPage === 1"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                  :class="customersPage === 1 ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <span class="text-xs text-slate-600 dark:text-slate-400 px-2">{{ customersPage }} / {{ customersTotalPages }}</span>
                <button @click="customersPage++" :disabled="customersPage === customersTotalPages"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                  :class="customersPage === customersTotalPages ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ───────────────────────────────────────────
           DESKTOP HEADER + CONTENT
      ─────────────────────────────────────────────── -->
      <div class="hidden md:block p-6">

        <!-- Header row -->
        <div class="flex items-center justify-between mb-5">
          <div>
            <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Customers Report</h1>
            <p class="text-slate-400 text-sm mt-0.5">{{ periodLabel }}</p>
          </div>

          <!-- Desktop filters -->
          <div class="flex items-center gap-2">
            <div class="relative">
              <select v-model="filters.preset"
                class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-2 text-sm font-medium
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option value="all_time">All Time</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom</option>
              </select>
              <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
            <template v-if="filters.preset === 'custom'">
              <input type="date" v-model="filters.from"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
              <span class="text-slate-400 text-sm">→</span>
              <input type="date" v-model="filters.to"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
            </template>
            <!-- City dropdown -->
            <div v-if="cities.length > 0" class="relative">
              <select v-model="desktopCityFilter"
                @change="handleDesktopCityChange"
                class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-2 text-sm font-medium
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option value="">All Cities</option>
                <option v-for="city in cities" :key="city" :value="city">{{ city }}</option>
              </select>
              <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- KPI cards row -->
        <div class="grid grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          <template v-if="loading">
            <div v-for="i in 5" :key="i"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 space-y-2">
              <div class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2 w-12 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
          </template>
          <template v-else>
            <div v-for="kpi in customerKPIs" :key="kpi.label"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
              <p class="text-sm font-bold leading-tight" :class="kpi.color">{{ kpi.value }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">{{ kpi.sub }}</p>
            </div>
          </template>
        </div>

        <!-- Charts row -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <!-- Doughnut -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Receivable vs Payable</p>
            <div v-if="loading" class="flex items-center justify-center h-40">
              <div class="w-40 h-40 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse"></div>
            </div>
            <div v-else class="flex items-center gap-6">
              <div class="shrink-0">
                <Doughnut :data="customerDoughnutData" :options="doughnutOptions" style="width:160px;height:160px" />
              </div>
              <div class="flex-1 space-y-2.5">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Receivable</span>
                  </div>
                  <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(kpis.total_receivable) }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Payable</span>
                  </div>
                  <span class="text-xs font-bold text-red-500 dark:text-red-400">{{ shortNum(kpis.total_payable) }}</span>
                </div>
                <div class="border-t border-slate-100 dark:border-slate-800 pt-2 flex items-center justify-between">
                  <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">Net Balance</span>
                  <span class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(kpis.net_balance) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- City distribution -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Customers by City</p>
            <div v-if="loading" class="flex items-end gap-1.5 h-40">
              <div v-for="i in 8" :key="i" class="flex-1 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"
                :style="{ height: (30 + Math.random() * 70) + '%' }"></div>
            </div>
            <Bar v-else :data="cityChartData" :options="cityBarOptions" style="max-height:160px" />
          </div>
        </div>

        <!-- Top performers row -->
        <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
          <!-- Top by sales -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-3">Top 5 by Sales</p>
            <div v-if="loading" class="space-y-2">
              <div v-for="i in 5" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
            </div>
            <div v-else-if="topBySales.length === 0" class="text-xs text-slate-400 text-center py-8">No data</div>
            <div v-else class="space-y-3">
              <div v-for="c in topBySales" :key="c.name">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                  <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400 ml-2 shrink-0">{{ shortNum(c.sales) }}</span>
                </div>
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full bg-indigo-500 rounded-full" :style="{ width: calculatePercentage(c.sales, topBySales[0].sales) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top by receivable -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-3">Top 5 by Receivable</p>
            <div v-if="loading" class="space-y-2">
              <div v-for="i in 5" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
            </div>
            <div v-else-if="topByReceivable.length === 0" class="text-xs text-slate-400 text-center py-8">No data</div>
            <div v-else class="space-y-3">
              <div v-for="c in topByReceivable" :key="c.name">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                  <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(c.receivable) }}</span>
                </div>
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full bg-emerald-500 rounded-full" :style="{ width: calculatePercentage(c.receivable, topByReceivable[0].receivable) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top by profit -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-3">Top 5 by Profit</p>
            <div v-if="loading" class="space-y-2">
              <div v-for="i in 5" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
            </div>
            <div v-else-if="topByProfit.length === 0" class="text-xs text-slate-400 text-center py-8">No data</div>
            <div v-else class="space-y-3">
              <div v-for="c in topByProfit" :key="c.name">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                  <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(c.profit) }}</span>
                </div>
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full bg-emerald-500 rounded-full" :style="{ width: calculatePercentage(c.profit, topByProfit[0].profit) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Lowest 5 by profit -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-3">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Lowest 5 by Profit</p>
              <span class="text-[10px] font-semibold bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full">Attention</span>
            </div>
            <div v-if="loading" class="space-y-2">
              <div v-for="i in 5" :key="i" class="h-12 bg-slate-50 dark:bg-slate-800 rounded animate-pulse"></div>
            </div>
            <div v-else-if="lowestByProfit.length === 0" class="text-xs text-slate-400 text-center py-8">No data</div>
            <div v-else class="space-y-3">
              <div v-for="c in lowestByProfit" :key="c.name">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                  <span class="text-xs font-bold ml-2 shrink-0"
                    :class="c.profit >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'">
                    {{ c.profit < 0 ? '-' : '' }}{{ shortNum(Math.abs(c.profit)) }}
                  </span>
                </div>
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full rounded-full"
                    :class="c.profit >= 0 ? 'bg-amber-400' : 'bg-red-500'"
                    :style="{ width: calculateLowestProfitPct(c.profit) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Inactive Customers Table -->
        <div class="mb-5">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Inactive Customers (No activity in last 30 days)
              <span v-if="!loading && filteredAndSortedInactive.length > 0" class="text-slate-500 font-normal ml-1">
                ({{ filteredAndSortedInactive.length }} total)
              </span>
            </p>
            <div class="flex items-center gap-3">
              <select v-if="!loading && inactiveCustomers.length > 0" v-model="inactivePerPage"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-lg px-2 py-1 text-xs
                       focus:outline-none focus:ring-2 focus:ring-amber-500 transition">
                <option :value="5">5 per page</option>
                <option :value="10">10 per page</option>
                <option :value="25">25 per page</option>
                <option :value="50">50 per page</option>
                <option :value="100">100 per page</option>
              </select>
              <div class="relative" v-if="!loading && inactiveCustomers.length > 0">
                <input type="text" v-model="inactiveSearch" placeholder="Search inactive..."
                  class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 rounded-xl pl-9 pr-3 py-1.5 text-xs
                         focus:outline-none focus:ring-2 focus:ring-amber-500 transition w-56">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div v-if="loading" class="text-xs text-slate-400 text-center py-8">Loading...</div>
            <div v-else-if="inactiveCustomers.length === 0" class="text-xs text-slate-400 text-center py-8">No inactive customers</div>
            <div v-else-if="filteredAndSortedInactive.length === 0" class="text-xs text-slate-400 text-center py-8">No results found</div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <th @click="sortInactive('name')" class="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="flex items-center gap-1">
                        Customer
                        <svg v-if="inactiveSortKey === 'name'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('city')" class="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="flex items-center gap-1">
                        City
                        <svg v-if="inactiveSortKey === 'city'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('business')" class="text-right px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Sales
                        <svg v-if="inactiveSortKey === 'business'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('receivable')" class="text-right px-5 py-3 text-[10px] font-semibold text-emerald-500 uppercase tracking-wide cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Receivable
                        <svg v-if="inactiveSortKey === 'receivable'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('payable')" class="text-right px-5 py-3 text-[10px] font-semibold text-red-500 uppercase tracking-wide cursor-pointer hover:text-red-600 dark:hover:text-red-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Payable
                        <svg v-if="inactiveSortKey === 'payable'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('balance')" class="text-right px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Balance
                        <svg v-if="inactiveSortKey === 'balance'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('profit')" class="text-right px-5 py-3 text-[10px] font-semibold text-violet-500 uppercase tracking-wide cursor-pointer hover:text-violet-600 dark:hover:text-violet-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Profit
                        <svg v-if="inactiveSortKey === 'profit'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('margin')" class="text-center px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Margin
                        <svg v-if="inactiveSortKey === 'margin'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('invoices')" class="text-center px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Orders
                        <svg v-if="inactiveSortKey === 'invoices'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('last_activity')" class="text-right px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Last Activity
                        <svg v-if="inactiveSortKey === 'last_activity'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortInactive('days_since')" class="text-right px-5 py-3 text-[10px] font-semibold text-amber-500 uppercase tracking-wide cursor-pointer hover:text-amber-600 dark:hover:text-amber-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Days Idle
                        <svg v-if="inactiveSortKey === 'days_since'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="inactiveSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
                  <tr v-for="c in paginatedInactiveCustomers" :key="c.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                    <td class="px-5 py-3.5">
                      <p class="font-semibold text-slate-800 dark:text-white">{{ c.name }}</p>
                      <p class="text-[10px] text-slate-400">{{ c.shop }}</p>
                    </td>
                    <td class="px-5 py-3.5 text-slate-600 dark:text-slate-400">{{ c.city }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-slate-700 dark:text-slate-300">{{ shortNum(c.business) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ shortNum(c.receivable) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-red-500 dark:text-red-400">{{ shortNum(c.payable) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold"
                      :class="c.balance > 0 ? 'text-emerald-600 dark:text-emerald-400' : c.balance < 0 ? 'text-red-500 dark:text-red-400' : 'text-slate-400'">
                      {{ c.balance > 0 ? '+' : c.balance < 0 ? '-' : '' }}{{ shortNum(Math.abs(c.balance)) }}
                    </td>
                    <td class="px-5 py-3.5 text-right font-semibold text-violet-600 dark:text-violet-400">{{ shortNum(c.profit) }}</td>
                    <td class="px-5 py-3.5 text-center">
                      <span class="text-xs font-semibold px-2 py-0.5 rounded-full"
                        :class="c.margin >= 30 ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400' :
                                c.margin >= 15 ? 'bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400' :
                                c.margin >= 0 ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400' :
                                'bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400'">
                        {{ c.margin }}%
                      </span>
                    </td>
                    <td class="px-5 py-3.5 text-center font-semibold text-slate-600 dark:text-slate-400">{{ c.invoices }}</td>
                    <td class="px-5 py-3.5 text-right text-slate-600 dark:text-slate-400">{{ c.last_activity || 'N/A' }}</td>
                    <td class="px-5 py-3.5 text-right font-bold text-amber-500">{{ c.days_since }}d</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Pagination for Inactive Customers -->
            <div v-if="!loading && filteredAndSortedInactive.length > 0 && inactiveTotalPages > 1"
              class="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
              <div class="text-xs text-slate-600 dark:text-slate-400">
                Showing {{ ((inactivePage - 1) * inactivePerPage) + 1 }} to {{ Math.min(inactivePage * inactivePerPage, filteredAndSortedInactive.length) }} of {{ filteredAndSortedInactive.length }} results
              </div>
              <div class="flex items-center gap-1">
                <button @click="inactivePage = 1" :disabled="inactivePage === 1"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="inactivePage === 1 ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/></svg>
                </button>
                <button @click="inactivePage--" :disabled="inactivePage === 1"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="inactivePage === 1 ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <template v-for="page in inactiveTotalPages" :key="page">
                  <button v-if="page === 1 || page === inactiveTotalPages || (page >= inactivePage - 1 && page <= inactivePage + 1)"
                    @click="inactivePage = page"
                    class="px-2.5 py-1 text-xs rounded transition"
                    :class="page === inactivePage
                      ? 'bg-amber-500 text-white font-semibold'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'">
                    {{ page }}
                  </button>
                  <span v-else-if="page === inactivePage - 2 || page === inactivePage + 2" class="px-1 text-slate-400">...</span>
                </template>
                <button @click="inactivePage++" :disabled="inactivePage === inactiveTotalPages"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="inactivePage === inactiveTotalPages ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
                <button @click="inactivePage = inactiveTotalPages" :disabled="inactivePage === inactiveTotalPages"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="inactivePage === inactiveTotalPages ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- All Customers Table -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              All Customers
              <span v-if="!loading && filteredAndSortedCustomers.length > 0" class="text-slate-500 font-normal ml-1">
                ({{ filteredAndSortedCustomers.length }} total)
              </span>
            </p>
            <div class="flex items-center gap-3">
              <select v-if="!loading && allCustomers.length > 0" v-model="customersPerPage"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-lg px-2 py-1 text-xs
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition">
                <option :value="5">5 per page</option>
                <option :value="10">10 per page</option>
                <option :value="25">25 per page</option>
                <option :value="50">50 per page</option>
                <option :value="100">100 per page</option>
              </select>
              <div class="relative" v-if="!loading && allCustomers.length > 0">
                <input type="text" v-model="customersSearch" placeholder="Search customers..."
                  class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 rounded-xl pl-9 pr-3 py-1.5 text-xs
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 transition w-56">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div v-if="loading" class="text-xs text-slate-400 text-center py-8">Loading...</div>
            <div v-else-if="allCustomers.length === 0" class="text-xs text-slate-400 text-center py-8">No customer data for this period.</div>
            <div v-else-if="filteredAndSortedCustomers.length === 0" class="text-xs text-slate-400 text-center py-8">No results found</div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <th @click="sortCustomers('name')" class="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="flex items-center gap-1">
                        Customer
                        <svg v-if="customersSortKey === 'name'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('city')" class="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="flex items-center gap-1">
                        City
                        <svg v-if="customersSortKey === 'city'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('business')" class="text-right px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Sales
                        <svg v-if="customersSortKey === 'business'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('receivable')" class="text-right px-5 py-3 text-[10px] font-semibold text-emerald-500 uppercase tracking-wide cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Receivable
                        <svg v-if="customersSortKey === 'receivable'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('payable')" class="text-right px-5 py-3 text-[10px] font-semibold text-red-500 uppercase tracking-wide cursor-pointer hover:text-red-600 dark:hover:text-red-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Payable
                        <svg v-if="customersSortKey === 'payable'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('balance')" class="text-right px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Balance
                        <svg v-if="customersSortKey === 'balance'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('profit')" class="text-right px-5 py-3 text-[10px] font-semibold text-violet-500 uppercase tracking-wide cursor-pointer hover:text-violet-600 dark:hover:text-violet-400 transition">
                      <span class="inline-flex items-center gap-1">
                        Profit
                        <svg v-if="customersSortKey === 'profit'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('margin')" class="text-center px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Margin
                        <svg v-if="customersSortKey === 'margin'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('invoices')" class="text-center px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Orders
                        <svg v-if="customersSortKey === 'invoices'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                    <th @click="sortCustomers('last_activity')" class="text-right px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition">
                      <span class="inline-flex items-center gap-1">
                        Last Activity
                        <svg v-if="customersSortKey === 'last_activity'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="customersSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
                        </svg>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
                  <tr v-for="c in paginatedAllCustomers" :key="c.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                    <td class="px-5 py-3.5">
                      <p class="font-semibold text-slate-800 dark:text-white">{{ c.name }}</p>
                      <p class="text-[10px] text-slate-400">{{ c.shop }}</p>
                    </td>
                    <td class="px-5 py-3.5 text-slate-600 dark:text-slate-400">{{ c.city }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-slate-700 dark:text-slate-300">{{ shortNum(c.business) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ shortNum(c.receivable) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-red-500 dark:text-red-400">{{ shortNum(c.payable) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold"
                      :class="c.balance > 0 ? 'text-emerald-600 dark:text-emerald-400' : c.balance < 0 ? 'text-red-500 dark:text-red-400' : 'text-slate-400'">
                      {{ c.balance > 0 ? '+' : c.balance < 0 ? '-' : '' }}{{ shortNum(Math.abs(c.balance)) }}
                    </td>
                    <td class="px-5 py-3.5 text-right font-semibold text-violet-600 dark:text-violet-400">{{ shortNum(c.profit) }}</td>
                    <td class="px-5 py-3.5 text-center">
                      <span class="text-xs font-semibold px-2 py-0.5 rounded-full"
                        :class="c.margin >= 25 ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400' : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'">
                        {{ c.margin }}%
                      </span>
                    </td>
                    <td class="px-5 py-3.5 text-center font-semibold text-indigo-600 dark:text-indigo-400">{{ c.invoices }}</td>
                    <td class="px-5 py-3.5 text-right text-slate-600 dark:text-slate-400">{{ formatLastActivity(c.last_activity) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Pagination for All Customers -->
            <div v-if="!loading && filteredAndSortedCustomers.length > 0 && customersTotalPages > 1"
              class="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
              <div class="text-xs text-slate-600 dark:text-slate-400">
                Showing {{ ((customersPage - 1) * customersPerPage) + 1 }} to {{ Math.min(customersPage * customersPerPage, filteredAndSortedCustomers.length) }} of {{ filteredAndSortedCustomers.length }} results
              </div>
              <div class="flex items-center gap-1">
                <button @click="customersPage = 1" :disabled="customersPage === 1"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="customersPage === 1 ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/></svg>
                </button>
                <button @click="customersPage--" :disabled="customersPage === 1"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="customersPage === 1 ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <template v-for="page in customersTotalPages" :key="page">
                  <button v-if="page === 1 || page === customersTotalPages || (page >= customersPage - 1 && page <= customersPage + 1)"
                    @click="customersPage = page"
                    class="px-2.5 py-1 text-xs rounded transition"
                    :class="page === customersPage
                      ? 'bg-indigo-500 text-white font-semibold'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'">
                    {{ page }}
                  </button>
                  <span v-else-if="page === customersPage - 2 || page === customersPage + 2" class="px-1 text-slate-400">...</span>
                </template>
                <button @click="customersPage++" :disabled="customersPage === customersTotalPages"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="customersPage === customersTotalPages ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
                <button @click="customersPage = customersTotalPages" :disabled="customersPage === customersTotalPages"
                  class="px-2 py-1 text-xs rounded transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700"
                  :class="customersPage === customersTotalPages ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>

    <!-- ── MOBILE FILTERS SHEET ── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileFiltersOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileFiltersOpen = false" />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileFiltersOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 82vh; padding-bottom: env(safe-area-inset-bottom, 0px)"
          @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-semibold text-slate-800 dark:text-white text-base">Filters</p>
            <div class="flex items-center gap-2">
              <button v-if="mobileFilterBadge > 0"
                @click="filters.preset = 'all_time'; filters.from = ''; filters.to = ''; filters.cities = []; mobileFiltersOpen = false"
                class="text-xs font-semibold text-red-500 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/50 transition">
                Reset
              </button>
              <button @click="mobileFiltersOpen = false"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Content -->
          <div class="overflow-y-auto flex-1 px-4 py-5 space-y-6">
            <!-- Period -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Period</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in presetOptions" :key="opt.v"
                  @click="filters.preset = opt.v"
                  class="text-sm font-semibold px-4 py-2 rounded-full transition active:scale-95"
                  :class="filters.preset === opt.v
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ opt.l }}
                </button>
              </div>
              <!-- Custom date range -->
              <div v-if="filters.preset === 'custom'" class="flex items-center gap-2 mt-3">
                <input type="date" v-model="filters.from"
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 text-sm rounded-xl px-3 py-2.5
                         focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <span class="text-slate-400 text-sm">→</span>
                <input type="date" v-model="filters.to"
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 text-sm rounded-xl px-3 py-2.5
                         focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <!-- Cities -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Cities</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="city in cities" :key="city"
                  @click="toggleCity(city)"
                  class="text-sm font-semibold px-4 py-2 rounded-full transition active:scale-95"
                  :class="filters.cities.includes(city)
                    ? 'bg-teal-500 text-white shadow-sm'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ city }}
                </button>
              </div>
              <p v-if="filters.cities.length > 0" class="text-xs text-slate-500 dark:text-slate-400 mt-3">
                {{ filters.cities.length }} {{ filters.cities.length === 1 ? 'city' : 'cities' }} selected
              </p>
              <p v-else class="text-xs text-slate-500 dark:text-slate-400 mt-3">All cities</p>
            </div>
          </div>
          <!-- Apply button -->
          <div class="px-4 py-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
            <button @click="mobileFiltersOpen = false"
              class="w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] text-white text-sm font-semibold transition shadow-sm shadow-emerald-500/30">
              Apply Filters
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

  </AppLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Doughnut, Bar } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => {
  document.title = `Customers Report | ${businessName.value}`
  fetchData()
})

const loading = ref(true)
const kpis = ref({})
const cities = ref([])
const cityDistribution = ref([])
const topBySales = ref([])
const topByReceivable = ref([])
const topByProfit = ref([])
const lowestByProfit = ref([])
const inactiveCustomers = ref([])
const allCustomers = ref([])

const mobileFiltersOpen = ref(false)
const inactiveSearch = ref('')
const customersSearch = ref('')
const desktopCityFilter = ref('')

// Sorting state
const inactiveSortKey = ref('days_since')
const inactiveSortDir = ref('desc')
const customersSortKey = ref('name')
const customersSortDir = ref('asc')

// Pagination state
const inactivePage = ref(1)
const inactivePerPage = ref(5)
const customersPage = ref(1)
const customersPerPage = ref(5)

const presetOptions = [
  { v: 'all_time',   l: 'All Time'   },
  { v: 'this_month', l: 'This Month' },
  { v: 'last_month', l: 'Last Month' },
  { v: 'this_year',  l: 'This Year'  },
  { v: 'last_year',  l: 'Last Year'  },
  { v: 'custom',     l: 'Custom'     },
]

const mobileFilterBadge = computed(() => {
  let count = 0
  if (filters.value.preset !== 'all_time') count++
  if (filters.value.cities.length > 0) count++
  return count
})

const filters = ref({
  preset: 'all_time',
  from: '',
  to: '',
  cities: [],
})

async function fetchData() {
  loading.value = true
  const params = new URLSearchParams({
    preset: filters.value.preset,
  })
  if (filters.value.preset === 'custom') {
    if (filters.value.from) params.set('from', filters.value.from)
    if (filters.value.to) params.set('to', filters.value.to)
  }
  if (filters.value.cities.length > 0) {
    params.set('cities', filters.value.cities.join(','))
  } else {
    params.set('cities', 'all')
  }
  try {
    const res = await fetch(`/reports/customers/data?${params}`, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
    const data = await res.json()
    kpis.value = data.kpis || {}
    cities.value = data.cities || []
    cityDistribution.value = data.city_distribution || []
    topBySales.value = data.top_by_sales || []
    topByReceivable.value = data.top_by_receivable || []
    topByProfit.value = data.top_by_profit || []
    lowestByProfit.value = data.lowest_by_profit || []
    inactiveCustomers.value = data.inactive_customers || []
    allCustomers.value = data.all_customers || []
  } finally {
    loading.value = false
  }
}

watch(filters, (val) => {
  if (val.preset === 'custom' && (!val.from || !val.to)) return
  fetchData()
}, { deep: true })

function toggleCity(city) {
  const idx = filters.value.cities.indexOf(city)
  if (idx > -1) {
    filters.value.cities.splice(idx, 1)
  } else {
    filters.value.cities.push(city)
  }
}

function handleDesktopCityChange() {
  if (desktopCityFilter.value) {
    filters.value.cities = [desktopCityFilter.value]
  } else {
    filters.value.cities = []
  }
}

function sortInactive(key) {
  if (inactiveSortKey.value === key) {
    inactiveSortDir.value = inactiveSortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    inactiveSortKey.value = key
    inactiveSortDir.value = 'asc'
  }
}

function sortCustomers(key) {
  if (customersSortKey.value === key) {
    customersSortDir.value = customersSortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    customersSortKey.value = key
    customersSortDir.value = 'asc'
  }
}

const periodLabel = computed(() => {
  const map = { all_time: 'All Time', this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year', custom: 'Custom Range' }
  return map[filters.value.preset] || 'Custom Range'
})

function shortNum(n) {
  n = Number(n) || 0
  return 'Rs ' + Math.floor(n).toLocaleString('en-PK')
}

function compactNum(n) {
  n = Number(n) || 0
  return 'Rs ' + Math.floor(n).toLocaleString('en-PK')
}

function calculatePercentage(value, max) {
  if (!max || max === 0) return 0
  return Math.min(100, Math.max(0, (value / max) * 100))
}

function calculateLowestProfitPct(profit) {
  if (lowestByProfit.value.length === 0) return 0
  const minProfit = Math.min(...lowestByProfit.value.map(c => c.profit))
  const maxProfit = Math.max(...lowestByProfit.value.map(c => c.profit))
  const range = maxProfit - minProfit
  if (range === 0) return 100
  return ((profit - minProfit) / range) * 100
}

function formatLastActivity(date) {
  if (!date) return 'Never'
  const d = new Date(date)
  const now = new Date()
  const diffTime = Math.abs(now - d)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return `${Math.floor(diffDays / 365)}y ago`
}

const tickColor = '#94a3b8'
const gridColor = 'rgba(148,163,184,0.12)'
const baseFont = { family: 'inherit', size: 10 }

// ── KPIs ─────────────────────────────────────────────
const customerKPIs = computed(() => [
  { label: 'Receivable', value: shortNum(kpis.value?.total_receivable || 0), color: 'text-emerald-600 dark:text-emerald-400', sub: `${kpis.value?.total_customers || 0} customers` },
  { label: 'Payable', value: shortNum(kpis.value?.total_payable || 0), color: 'text-red-500 dark:text-red-400', sub: '' },
  { label: 'Net Balance', value: shortNum(kpis.value?.net_balance || 0), color: 'text-slate-800 dark:text-white', sub: 'In your favour' },
  { label: 'Total Customers', value: kpis.value?.total_customers || 0, color: 'text-indigo-600 dark:text-indigo-400', sub: 'Active' },
  { label: 'Inactive', value: kpis.value?.inactive_count || 0, color: 'text-amber-500', sub: 'Last 30 days' },
])

// ── Doughnut ─────────────────────────────────────────
const doughnutOptions = {
  responsive: false,
  plugins: { legend: { display: false } },
  cutout: '70%',
}
const customerDoughnutData = computed(() => ({
  labels: ['Receivable', 'Payable'],
  datasets: [{
    data: [kpis.value?.total_receivable || 0, kpis.value?.total_payable || 0],
    backgroundColor: ['rgba(16,185,129,0.85)', 'rgba(239,68,68,0.85)'],
    borderWidth: 0
  }],
}))

// ── City chart ───────────────────────────────────────
const cityBarOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, stepSize: 2 } },
  },
}
const cityChartData = computed(() => ({
  labels: cityDistribution.value.map(c => c.city),
  datasets: [{ data: cityDistribution.value.map(c => c.count), backgroundColor: 'rgba(16,185,129,0.85)', borderRadius: 6 }],
}))

// ── Search Filters with Sorting ───────────────────────────────────
const filteredAndSortedInactive = computed(() => {
  let filtered = inactiveCustomers.value

  // Apply search filter
  if (inactiveSearch.value.trim()) {
    const search = inactiveSearch.value.toLowerCase()
    filtered = filtered.filter(c =>
      c.name.toLowerCase().includes(search) ||
      (c.shop && c.shop.toLowerCase().includes(search)) ||
      (c.city && c.city.toLowerCase().includes(search))
    )
  }

  // Apply sorting
  const key = inactiveSortKey.value
  const dir = inactiveSortDir.value
  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[key]
    let bVal = b[key]

    // Handle null/undefined
    if (aVal == null) aVal = ''
    if (bVal == null) bVal = ''

    // String comparison
    if (typeof aVal === 'string') {
      return dir === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal)
    }

    // Number comparison
    return dir === 'asc' ? aVal - bVal : bVal - aVal
  })

  return sorted
})

const filteredAndSortedCustomers = computed(() => {
  let filtered = allCustomers.value

  // Apply search filter
  if (customersSearch.value.trim()) {
    const search = customersSearch.value.toLowerCase()
    filtered = filtered.filter(c =>
      c.name.toLowerCase().includes(search) ||
      (c.shop && c.shop.toLowerCase().includes(search)) ||
      (c.city && c.city.toLowerCase().includes(search))
    )
  }

  // Apply sorting
  const key = customersSortKey.value
  const dir = customersSortDir.value
  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[key]
    let bVal = b[key]

    // Handle null/undefined
    if (aVal == null) aVal = ''
    if (bVal == null) bVal = ''

    // String comparison
    if (typeof aVal === 'string') {
      return dir === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal)
    }

    // Number comparison
    return dir === 'asc' ? aVal - bVal : bVal - aVal
  })

  return sorted
})

// ── Pagination ───────────────────────────────────
const inactiveTotalPages = computed(() => Math.ceil(filteredAndSortedInactive.value.length / inactivePerPage.value))
const customersTotalPages = computed(() => Math.ceil(filteredAndSortedCustomers.value.length / customersPerPage.value))

const paginatedInactiveCustomers = computed(() => {
  const start = (inactivePage.value - 1) * inactivePerPage.value
  const end = start + inactivePerPage.value
  return filteredAndSortedInactive.value.slice(start, end)
})

const paginatedAllCustomers = computed(() => {
  const start = (customersPage.value - 1) * customersPerPage.value
  const end = start + customersPerPage.value
  return filteredAndSortedCustomers.value.slice(start, end)
})

// Watch for search/sort/perPage changes and reset to page 1
watch([inactiveSearch, inactiveSortKey, inactiveSortDir, inactivePerPage], () => {
  inactivePage.value = 1
})

watch([customersSearch, customersSortKey, customersSortDir, customersPerPage], () => {
  customersPage.value = 1
})
</script>

<style scoped>
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>
